/**
 * Enhances the customer reviews carousel.
 *
 * Paging, scroll snapping and arrow states come from Dawn's <slider-component>.
 * This adds the pagination dots (whose count depends on how many cards fit at
 * the current breakpoint) and optional autoplay that yields to the shopper.
 */
(() => {
  if (customElements.get('sn-review-carousel')) return;

  class ReviewCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.slider = this.querySelector('slider-component');
      this.track = this.querySelector('[id^="Slider-"]');
      this.dotsHost = this.querySelector('[data-sn-dots]');
      if (!this.slider || !this.track) return;

      this.intervalMs = (Number(this.dataset.interval) || 6) * 1000;
      this.autoplayWanted = this.dataset.autoplay === 'true';
      this.engaged = false;
      this.visible = true;
      this.hovered = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      customElements.whenDefined('slider-component').then(() => {
        requestAnimationFrame(() => this.setup());
      });
    }

    disconnectedCallback() {
      this.stop();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }

    setup() {
      this.slider.addEventListener('slideChanged', () => this.syncDots());

      this.resizeObserver = new ResizeObserver(() => this.buildDots());
      this.resizeObserver.observe(this.track);

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.start() : this.stop();
        },
        { threshold: 0.25 }
      );
      this.intersectionObserver.observe(this);

      // A deliberate interaction hands control to the shopper for good.
      ['pointerdown', 'keydown'].forEach((type) =>
        this.addEventListener(type, () => this.disengageAutoplay(), { passive: true })
      );
      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.stop();
      });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.start();
      });
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
      this.reducedMotion.addEventListener('change', () => (this.reducedMotion.matches ? this.stop() : this.start()));

      this.buildDots();
      this.start();
    }

    /**
     * Derived from scroll geometry rather than SliderComponent.totalPages,
     * which overcounts by one when the track uses a `gap` (the offset between
     * cards then exceeds the visible width on single-card breakpoints).
     */
    get totalPages() {
      const offset = this.slider.sliderItemOffset;
      const items = this.slider.sliderItemsToShow?.length || 1;
      if (!offset) return 1;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll <= 1) return 1;

      return Math.min(items, Math.max(1, Math.round(maxScroll / offset) + 1));
    }

    get currentPage() {
      return Math.min(this.totalPages, Math.max(1, this.slider.currentPage || 1));
    }

    buildDots() {
      if (!this.dotsHost) return;
      const total = this.totalPages;

      if (this.dotsHost.childElementCount !== total) {
        this.dotsHost.replaceChildren();
        for (let i = 0; i < total; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'sn-reviews__dot';
          dot.setAttribute('aria-label', `Go to page ${i + 1} of ${total}`);
          dot.setAttribute('aria-controls', this.track.id);
          dot.addEventListener('click', () => {
            this.disengageAutoplay();
            this.goTo(i + 1);
          });
          this.dotsHost.appendChild(dot);
        }
      }

      this.dotsHost.toggleAttribute('hidden', total < 2);
      this.syncDots();
    }

    syncDots() {
      if (!this.dotsHost) return;
      const current = this.currentPage;
      Array.from(this.dotsHost.children).forEach((dot, index) => {
        const active = index === current - 1;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    goTo(page) {
      const offset = this.slider.sliderItemOffset;
      if (!offset) return;
      this.track.scrollTo({ left: (page - 1) * offset, behavior: 'smooth' });
    }

    advance() {
      const next = this.currentPage >= this.totalPages ? 1 : this.currentPage + 1;
      this.goTo(next);
    }

    start() {
      if (
        !this.autoplayWanted ||
        this.engaged ||
        this.hovered ||
        !this.visible ||
        document.hidden ||
        this.reducedMotion.matches ||
        this.totalPages < 2
      ) {
        return;
      }
      this.stop();
      this.timer = setInterval(() => this.advance(), this.intervalMs);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    disengageAutoplay() {
      this.engaged = true;
      this.stop();
    }
  }

  customElements.define('sn-review-carousel', ReviewCarousel);
})();
